class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    const details = this.mainDetailsToggle;
    const summary = details.querySelector('summary');
    summary.setAttribute('aria-expanded', false);

    if (!details.classList.contains('mega-menu') || !details.hasAttribute('open')) {
      details.removeAttribute('open');
      return;
    }

    if (this.closingTimer) {
      clearTimeout(this.closingTimer);
    }

    details.classList.add('mega-menu--closing');
    const duration = this.getTransitionDuration();
    this.closingTimer = setTimeout(() => {
      details.removeAttribute('open');
      details.classList.remove('mega-menu--closing');
      this.closingTimer = null;
    }, duration);
  }

  getTransitionDuration() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return 0;
    const content = this.content;
    if (!content) return 200;
    const value = getComputedStyle(content).transitionDuration;
    const seconds = parseFloat(value);
    return Number.isFinite(seconds) ? seconds * 1000 : 200;
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.openedByHover = false;

    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.summary.addEventListener('click', this.onSummaryClick);

    if (this.header) {
      HeaderHoverController.register(this.header).add(this);
    }
  }

  openFromHover() {
    if (this.closingTimer) {
      clearTimeout(this.closingTimer);
      this.closingTimer = null;
      this.mainDetailsToggle.classList.remove('mega-menu--closing');
    }
    if (this.mainDetailsToggle.open) return;
    this.openedByHover = true;
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', 'true');
  }

  closeFromHover() {
    if (!this.mainDetailsToggle.open) return;
    if (this.contains(document.activeElement)) return;
    this.openedByHover = false;
    this.close();
  }

  onSummaryClick(event) {
    if (this.openedByHover && this.mainDetailsToggle.open) {
      event.preventDefault();
      this.openedByHover = false;
      if (this.header && this.header.headerHoverController) {
        this.header.headerHoverController.setActive(null);
      }
      this.close();
    } else {
      this.openedByHover = false;
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

class HeaderHoverController {
  static register(header) {
    if (!header.headerHoverController) {
      header.headerHoverController = new HeaderHoverController(header);
    }
    return header.headerHoverController;
  }

  constructor(header) {
    this.header = header;
    this.menus = new Set();
    this.activeMenu = null;
    this.hoverMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.onPointerOver = this.onPointerOver.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);

    this.applyListeners(this.hoverMediaQuery.matches);
    this.hoverMediaQuery.addEventListener('change', this.onMediaChange);
  }

  add(menu) {
    this.menus.add(menu);
  }

  onMediaChange(event) {
    this.applyListeners(event.matches);
    if (!event.matches) this.setActive(null);
  }

  applyListeners(enable) {
    if (enable) {
      this.header.addEventListener('pointerover', this.onPointerOver);
      this.header.addEventListener('pointerleave', this.onPointerLeave);
    } else {
      this.header.removeEventListener('pointerover', this.onPointerOver);
      this.header.removeEventListener('pointerleave', this.onPointerLeave);
    }
  }

  onPointerOver(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!(event.target instanceof Element)) return;

    const menu = event.target.closest('header-menu');
    if (menu && this.menus.has(menu)) {
      this.setActive(menu);
      return;
    }

    if (this.isInteractiveHeaderItem(event.target)) {
      this.setActive(null);
    }
  }

  isInteractiveHeaderItem(target) {
    return Boolean(
      target.closest(
        'a, button, summary, [role="button"], .header__icon, .header__heading-logo-wrapper, .header__search, localization-form, header-drawer'
      )
    );
  }

  onPointerLeave(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.setActive(null);
  }

  setActive(menu) {
    if (menu === this.activeMenu) return;
    const previous = this.activeMenu;
    this.activeMenu = menu;
    if (previous) previous.closeFromHover();
    if (menu) menu.openFromHover();
  }
}

customElements.define('header-menu', HeaderMenu);
